// Playback engine: a look-ahead beat clock + oscillator tone scheduler.
// Drives score-follow highlight (onBeat) and reference tones (FR-4/FR-5).
// Tones are synthesized with OscillatorNode (MVP stand-in for SoundFont).

import type { AccompanimentTimeline, NoteEvent } from '../types';

export interface PlaybackCallbacks {
  onBeat?: (beat: number) => void;
  onEnd?: () => void;
}

export class PlaybackEngine {
  private ctx: AudioContext;
  private bpm: number;
  private rate = 1;
  private midiOffset: number; // ms
  private startCtxTime = 0;
  private startBeat = 0;
  private totalBeats: number;
  private running = false;
  private rafId = 0;
  private scheduled = new Set<number>(); // ctx times already scheduled (coarse)
  private melody: { beat: number; midi: number; dur: number }[] = [];
  private arp: AccompanimentTimeline | null = null;
  private metronome = true;
  private cb: PlaybackCallbacks;
  private master: GainNode;

  constructor(ctx: AudioContext, bpm: number, totalBeats: number, midiOffset: number, cb: PlaybackCallbacks = {}) {
    this.ctx = ctx;
    this.bpm = bpm;
    this.totalBeats = totalBeats;
    this.midiOffset = midiOffset;
    this.cb = cb;
    this.master = ctx.createGain();
    this.master.gain.value = 0.6;
    this.master.connect(ctx.destination);
  }

  setMelody(notes: { note: NoteEvent; measure: number }[]): void {
    this.melody = notes.map((n) => ({ beat: n.note.startTime, midi: n.note.pitch, dur: n.note.duration }));
  }

  setArpeggio(arp: AccompanimentTimeline | null): void {
    this.arp = arp;
  }

  setMetronome(on: boolean): void {
    this.metronome = on;
  }

  setRate(rate: number): void {
    if (this.running) {
      // Re-anchor so current beat is preserved.
      const cur = this.currentBeat();
      this.startBeat = cur;
      this.startCtxTime = this.ctx.currentTime;
    }
    this.rate = rate;
  }

  get isRunning(): boolean {
    return this.running;
  }

  currentBeat(): number {
    if (!this.running) return this.startBeat;
    const elapsed = this.ctx.currentTime - this.startCtxTime;
    return this.startBeat + (elapsed * (this.bpm * this.rate)) / 60;
  }

  start(fromBeat = 0): void {
    if (this.running) return;
    this.startCtxTime = this.ctx.currentTime;
    this.startBeat = fromBeat;
    this.running = true;
    this.scheduled.clear();
    this.loop();
  }

  pause(): void {
    if (!this.running) return;
    this.startBeat = this.currentBeat();
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  stop(): void {
    this.running = false;
    this.startBeat = 0;
    cancelAnimationFrame(this.rafId);
  }

  seek(beat: number): void {
    this.startBeat = beat;
    this.startCtxTime = this.ctx.currentTime;
    this.scheduled.clear();
  }

  private loop = (): void => {
    if (!this.running) return;
    const beat = this.currentBeat();
    if (beat >= this.totalBeats) {
      this.running = false;
      this.cb.onEnd?.();
      return;
    }
    // Schedule tones up to 0.15s ahead.
    const horizon = this.ctx.currentTime + 0.15;
    this.scheduleMelody(horizon);
    this.scheduleArp(horizon);
    this.scheduleMetronome(beat, horizon);
    this.cb.onBeat?.(beat);
    this.rafId = requestAnimationFrame(this.loop);
  };

  private beatToCtxTime(beat: number): number {
    const beatsFromStart = beat - this.startBeat;
    return this.startCtxTime + (beatsFromStart * 60) / (this.bpm * this.rate);
  }

  private scheduleMelody(horizon: number): void {
    for (const m of this.melody) {
      const key = Math.round(m.beat * 100);
      if (this.scheduled.has(key)) continue;
      const t = this.beatToCtxTime(m.beat) + this.midiOffset / 1000;
      if (t < this.ctx.currentTime - 0.05) {
        this.scheduled.add(key);
        continue;
      }
      if (t <= horizon) {
        this.scheduled.add(key);
        this.playTone(m.midi, t, (m.dur * 60) / (this.bpm * this.rate) * 0.9, 0.35, 'triangle');
      }
    }
  }

  private scheduleArp(horizon: number): void {
    if (!this.arp) return;
    for (const n of this.arp.notes) {
      const key = 100000 + Math.round(n.time * 100);
      if (this.scheduled.has(key)) continue;
      const t = this.beatToCtxTime(n.time) + this.midiOffset / 1000;
      if (t < this.ctx.currentTime - 0.05) {
        this.scheduled.add(key);
        continue;
      }
      if (t <= horizon) {
        this.scheduled.add(key);
        this.playTone(n.pitch, t, (n.duration * 60) / (this.bpm * this.rate) * 0.5, 0.2, 'sine');
      }
    }
  }

  private scheduleMetronome(beat: number, horizon: number): void {
    if (!this.metronome) return;
    const beatsPerMeasure = 4;
    // Schedule the next integer beat click if within horizon.
    const nextInt = Math.ceil(beat);
    for (let b = nextInt; b < nextInt + 3; b++) {
      const key = 200000 + b;
      if (this.scheduled.has(key)) continue;
      const t = this.beatToCtxTime(b);
      if (t > horizon || t < this.ctx.currentTime - 0.05) continue;
      this.scheduled.add(key);
      const accent = b % beatsPerMeasure === 0;
      this.click(t, accent);
    }
  }

  private midiToFreq(midi: number): number {
    return 440 * Math.pow(2, (midi - 69) / 12);
  }

  private playTone(midi: number, when: number, dur: number, vel: number, type: OscillatorType): void {
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.value = this.midiToFreq(midi);
    g.gain.setValueAtTime(0, when);
    g.gain.linearRampToValueAtTime(vel, when + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
    osc.connect(g).connect(this.master);
    osc.start(when);
    osc.stop(when + dur + 0.02);
  }

  private click(when: number, accent: boolean): void {
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = accent ? 1600 : 1000;
    g.gain.setValueAtTime(0.0001, when);
    g.gain.linearRampToValueAtTime(accent ? 0.25 : 0.15, when + 0.001);
    g.gain.exponentialRampToValueAtTime(0.0001, when + 0.05);
    osc.connect(g).connect(this.master);
    osc.start(when);
    osc.stop(when + 0.06);
  }
}
